import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../utils/api';
import { Calendar, AlertTriangle, ClipboardList } from 'lucide-react';
import SeatMemoCard, { RESULT_COLORS, canEditMemo } from '../components/SeatMemoCard';
import SeatMemoEditor from './SeatMemoEditor';
import { buildSeatMemoQuery } from '../utils/seat-memo-filter.mjs';

// 着座メモを期間でまとめて見る画面（ADR 0025）。
// 一般ユーザーは自分の分だけ。管理者は担当者を選べて「全員」も見られる。
// 全員分はサーバ側で期間が必須・上限31日（docs/bugs/2026-09-02 の読み取り行数対策）。

const card = {
  background: 'var(--field)',
  border: '1px solid var(--rule-strong)',
  boxShadow: 'none',
  borderRadius: 16,
  padding: '16px 18px',
  marginBottom: 12,
};

const inputStyle = { padding: '10px 12px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, minHeight: 44 };

// 日付入力は左右の余白を詰める。320px 幅だと 12px では「2026/09/20」が切れる。
// 文字サイズは 14px のまま（小さくして読めなくしない）。
const dateInputStyle = { ...inputStyle, padding: '10px 6px', flex: 1, minWidth: 0 };

const DEFAULT_RANGE_DAYS = 7; // 既定は今日を含む直近7日

const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
const shiftDate = (d, n) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

const WD = ['日', '月', '火', '水', '木', '金', '土'];
const dateLabel = (d) => {
  const dt = new Date(`${d}T00:00:00Z`);
  return `${dt.getUTCMonth() + 1}月${dt.getUTCDate()}日(${WD[dt.getUTCDay()]})`;
};

const RESULTS = ['獲得', '失注', '保留', '後追い', '未対応'];

export default function SeatMemoHistory({ user }) {
  const isAdmin = user?.role === 'admin';
  const today = todayJst();

  // 期間は最初から埋める。空（＝全期間）で開くと読み取り行数が読めなくなるため。
  const [filter, setFilter] = useState({
    user_id: isAdmin ? 'all' : '',
    date_from: shiftDate(today, -(DEFAULT_RANGE_DAYS - 1)),
    date_to: today,
    result: '',
  });
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false); // 一度でも読めたか（初回とエラー後を区別する）
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.getUsers().then(list => setUsers(Array.isArray(list) ? list : [])).catch(() => {});
  }, [isAdmin]);

  // 条件が揃っているか。揃っていなければ問い合わせず、理由をフィルタの下に出す。
  const query = useMemo(() => buildSeatMemoQuery(filter, { isAdmin }), [filter, isAdmin]);

  const load = useCallback(async () => {
    if (!query.ready) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api.getSeatMemos(query.params);
      setItems(Array.isArray(data) ? data : []);
      setLoaded(true);
      setError('');
    } catch (e) {
      // 読み込めなくても、いま出ている一覧は消さない。
      // 消すと「履歴が見られなくなった」に見えるうえ、再読み込みしか手が無くなる。
      setError(e.message || '読み込みに失敗しました');
    } finally { setLoading(false); }
  }, [query]);

  // フィルタを触るたびに即問い合わせると、日付をいじっている途中の値まで飛ぶ。
  // 少し待ってから投げる（D1の読み取り行数も減る）。
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleDelete = async (memo) => {
    const who = memo.user_name || `#${memo.user_id}`;
    if (!window.confirm(`${who} さんの ${memo.memo_date} の着座メモを削除します。よろしいですか？\n（この操作は取り消せません）`)) return;
    try { await api.deleteSeatMemo(memo.id); load(); } catch (e) { alert(e.message); }
  };

  // 日付ごとの括りに再構成（新しい日が上）。
  const groups = useMemo(() => {
    const map = {};
    items.forEach(m => { (map[m.memo_date] = map[m.memo_date] || []).push(m); });
    return Object.keys(map)
      .sort((a, b) => (a < b ? 1 : -1))
      .map(date => ({
        date,
        rows: map[date].slice().sort((a, b) => (a.memo_time || '').localeCompare(b.memo_time || '')),
      }));
  }, [items]);

  const total = items.length;
  const acqTotal = items.filter(m => m.result === '獲得').length;
  const validTotal = items.filter(m => m.valid_deal === 1).length;

  const setRange = (days) => setFilter(f => ({ ...f, date_from: shiftDate(today, -(days - 1)), date_to: today }));

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--ink)' }}>着座メモ 履歴</h1>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginBottom: 16, lineHeight: 1.6 }}>
        {isAdmin
          ? '過去の着座メモを期間でまとめて確認できます。カードの鉛筆から内容を直せます（日付の直しも編集画面の中でできます）。'
          : '自分が関わった着座メモを期間でまとめて確認できます。カードの鉛筆から内容を直せます。'}
      </div>

      {/* フィルター。
          From と To は「期間」というひとつの塊にして、折り返しでも必ず横に並べる
          （以前はスマホで From が1行目の右端、To が2行目の左端に離れて読めなかった）。 */}
      <div style={{ ...card, padding: '12px 14px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {isAdmin && (
          <Field label="担当者" style={{ flex: '1 1 150px' }}>
            <select value={filter.user_id} onChange={e => setFilter(f => ({ ...f, user_id: e.target.value }))} style={{ ...inputStyle, width: '100%' }}>
              <option value="all">全員</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        )}

        {/* 期間（From 〜 To を1つの塊として折り返す） */}
        <Field label="期間" style={{ flex: '2 1 250px' }}>
          <div className="date-range" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="date" value={filter.date_from} max={filter.date_to || today}
              onChange={e => setFilter(f => ({ ...f, date_from: e.target.value }))}
              style={dateInputStyle} />
            <span className="date-range__sep" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', flexShrink: 0 }}>〜</span>
            <input type="date" value={filter.date_to} max={today}
              onChange={e => setFilter(f => ({ ...f, date_to: e.target.value }))}
              style={dateInputStyle} />
          </div>
        </Field>

        <Field label="結果" style={{ flex: '1 1 110px' }}>
          <select value={filter.result} onChange={e => setFilter(f => ({ ...f, result: e.target.value }))} style={{ ...inputStyle, width: '100%' }}>
            <option value="">すべて</option>
            {RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>

        {/* よく使う期間。スマホでは1行使い切って、指で押せる幅を確保する。 */}
        <div style={{ display: 'flex', gap: 6, flex: '1 1 100%' }}>
          {[7, 14, 31].map(d => (
            <button key={d} onClick={() => setRange(d)} style={{ flex: 1, maxWidth: 160, padding: '10px 6px', minHeight: 44, borderRadius: 10, border: '1px solid var(--rule-strong)', background: 'var(--paper-sunk)', color: 'var(--ink-soft)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              直近{d}日
            </button>
          ))}
        </div>
      </div>

      {/* 合計 */}
      {/* 条件が揃っていないときは、問い合わせずにその場で理由を出す。
          一覧はそのまま残す（消すと「履歴が見られない」に見えるため）。 */}
      {!query.ready && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16, padding: '12px 14px', borderRadius: 12, background: 'var(--hold-bg)', border: '1px solid #5C4718' }}>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 700, flex: '1 1 200px', lineHeight: 1.6 }}>{query.reason}</span>
          <button onClick={() => setRange(DEFAULT_RANGE_DAYS)} style={recoverBtn}>直近{DEFAULT_RANGE_DAYS}日に戻す</button>
        </div>
      )}

      {/* 読み込みに失敗したとき。一覧は消さず、上に理由と再試行を出す。 */}
      {query.ready && error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16, padding: '12px 14px', borderRadius: 12, background: 'color-mix(in srgb, var(--loss) 9%, transparent)', border: '1px solid color-mix(in srgb, var(--loss) 27%, transparent)' }}>
          <AlertTriangle size={18} color="var(--loss)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--loss)', fontWeight: 700, flex: '1 1 200px', lineHeight: 1.6 }}>{error}</span>
          <button onClick={load} style={recoverBtn}>再試行</button>
        </div>
      )}

      {total > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, opacity: query.ready ? 1 : 0.5 }}>
          <Stat label="着座" value={total} color="var(--mark)" />
          <Stat label="獲得" value={acqTotal} color="var(--gain)" />
          <Stat label="有効商談" value={validTotal} color="var(--gain)" />
        </div>
      )}

      {loading && !loaded ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 40 }}>読み込み中...</div>
      ) : groups.length === 0 ? (
        // 0件と言い切れるのは、条件が揃っていて、エラーも出ていないときだけ。
        loaded && query.ready && !error ? (
          <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 60 }}>
            <ClipboardList size={28} color="var(--ink-mute)" style={{ marginBottom: 'var(--s3)' }} />
            <div style={{ fontSize: 14 }}>該当する着座メモがありません</div>
          </div>
        ) : null
      ) : (
        <div style={{ opacity: query.ready ? 1 : 0.5 }}>{groups.map(g => {
          const dayAcq = g.rows.filter(m => m.result === '獲得').length;
          return (
            <div key={g.date} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--rule-strong)', flexWrap: 'wrap' }}>
                <Calendar size={16} color="#33406E" />
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{dateLabel(g.date)}</span>
                <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>着座 {g.rows.length}件</span>
                {dayAcq > 0 && <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: RESULT_COLORS['獲得'] }}>獲得 {dayAcq}件</span>}
              </div>
              {g.rows.map(memo => (
                <SeatMemoCard
                  key={memo.id}
                  memo={memo}
                  user={user}
                  showOwner
                  onEdit={canEditMemo(memo, user) ? () => setEditor(memo) : undefined}
                  // 是正のための画面なので、管理者は他人のメモも消せる。確認で誰の何日ぶんかを名指しする。
                  onDelete={(isAdmin || memo.user_id === user?.id) ? handleDelete : undefined}
                />
              ))}
            </div>
          );
        })}</div>
      )}

      {editor !== null && (
        <SeatMemoEditor
          user={user}
          memo={editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </div>
  );
}

const recoverBtn = {
  padding: '10px 16px', minHeight: 44, borderRadius: 10, flexShrink: 0,
  border: '1.5px solid #33406E', background: 'var(--mark-soft)',
  color: 'var(--mark)', fontWeight: 700, fontSize: 'var(--t-note)', cursor: 'pointer',
};

// ラベル＋入力のひと組。折り返しの単位でもある。
function Field({ label, style, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, ...style }}>
      <label style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ padding: '8px 14px', background: 'var(--field)', border: '1px solid var(--rule-strong)', borderRadius: 12, minWidth: 84 }}>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
